function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

export const SESSION_RECORDING_CONFIG = {
  // 기본 설정
  enabled: !isLocalhost(),
  sampleRate: 0.5,

  // PII 마스킹 - 선택자
  maskSelectors: [
    // 입력 필드
    'input[type="password"]',
    'input[type="email"]',
    'input[type="tel"]',
    'input[type="number"]',

    // 데이터 속성
    "[data-ph-mask]",
    '[data-sensitive="true"]',

    // CSS 클래스
    ".password-input",
    ".credit-card-input",
    ".ssn-input",
    ".ph-mask",
    ".sensitive-field",

    // 폼 그룹
    '.form-group[data-field="password"]',
    '.form-group[data-field="creditCard"]',
    '.form-group[data-field="ssn"]',
  ],

  // 마스킹하지 않을 요소
  unmaskSelectors: [
    ".error-message",
    ".hint-text",
    ".help-text",
    "label",
    ".label",
    "button",
    "a",
  ],

  // 네트워크 요청 마스킹
  networkPayloadAllowList: [
    "https://api.searchright.net/search",
    "https://api.searchright.net/forms",
    "https://api.searchright.net/candidates",
  ],

  // 녹화 품질
  videoDimensions: {
    width: 1920,
    height: 1080,
  },

  // 프레임 레이트 (초당 프레임)
  frameRate: 10,

  // 콘솔 로그 녹화
  recordConsoleLog: true,

  // 네트워크 요청/응답 녹화 안함
  recordRequestData: false,
  recordResponseData: false,

  // 대역폭 절감
  compressSnapshot: true,
};

// 사용자 세그먼트별 샘플링 정책
export const SAMPLING_POLICY = {
  // 이탈 위험 사용자: 100% 녹화 (분석 우선순위 높음)
  highChurnRisk: 1.0,

  // 위험 사용자: 80% 녹화
  mediumChurnRisk: 0.8,

  // 활성 사용자: 20% 샘플링 (저렴한 분석)
  activeUsers: 0.2,

  // 신규 사용자: 50% 샘플링 (온보딩 분석)
  newUsers: 0.5,

  // 기본값: 50%
  default: 0.5,
};

// 민감한 정보 패턴 (정규식)
export const PII_PATTERNS = {
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  phone: /(\d{3})-?(\d{4})-?(\d{4})/g,
  ssn: /(\d{2})-?(\d{6})-?(\d{7})/g,
  creditCard: /\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}/g,
  koreanPhone: /0\d{1,2}-?\d{3,4}-?\d{4}/g,
};

// PII 마스킹 함수들
export const PII_MASKING = {
  maskEmail: (email: string): string => {
    const [local, domain] = email.split("@");
    const maskedLocal =
      local.charAt(0) + "***" + local.charAt(local.length - 1);
    const [name, ...domainParts] = domain.split(".");
    const maskedDomain =
      name.charAt(0) + "***" + name.slice(-1) + "." + domainParts.join(".");
    return `${maskedLocal}@${maskedDomain}`;
  },

  maskPhone: (phone: string): string => {
    return phone.replace(/(\d{3})-?(\d{4})-?(\d{4})/, "$1-****-****");
  },

  maskSSN: (ssn: string): string => {
    return ssn.replace(/(\d{2})-?(\d{6})-?(\d{7})/, "$1-****-*****");
  },

  maskCreditCard: (card: string): string => {
    const cleaned = card.replace(/\s|-/g, "");
    return "**** **** **** " + cleaned.slice(-4);
  },

  maskGeneric: (value: string): string => {
    if (value.length <= 2) return value;
    const first = value.charAt(0);
    const last = value.charAt(value.length - 1);
    const middle = "*".repeat(Math.max(1, value.length - 2));
    return first + middle + last;
  },
};

// 데이터 종류별 마스킹 함수 선택
export function getMaskingFunction(fieldType: string) {
  switch (fieldType.toLowerCase()) {
    case "email":
      return PII_MASKING.maskEmail;
    case "phone":
    case "tel":
      return PII_MASKING.maskPhone;
    case "ssn":
      return PII_MASKING.maskSSN;
    case "creditcard":
    case "card":
      return PII_MASKING.maskCreditCard;
    default:
      return PII_MASKING.maskGeneric;
  }
}

// 녹화 샘플링 결정
export function shouldRecordSession(
  userChurnRisk: "high" | "medium" | "low",
): boolean {
  let samplingRate = SAMPLING_POLICY.default;

  if (userChurnRisk === "high") {
    samplingRate = SAMPLING_POLICY.highChurnRisk;
  } else if (userChurnRisk === "medium") {
    samplingRate = SAMPLING_POLICY.mediumChurnRisk;
  } else {
    samplingRate = SAMPLING_POLICY.activeUsers;
  }

  return Math.random() < samplingRate;
}

// 페이지별 녹화 활성화 여부
export const PAGE_RECORDING_RULES = {
  // 녹화 활성화 페이지
  enabled: [
    "/request", // 요청 폼 (모든 단계)
    "/search", // 검색/필터링
    "/candidates", // 결과 페이지
  ],

  // 녹화 비활성화 페이지 (민감한 정보)
  disabled: [
    "/login", // 로그인 (인증정보)
    "/register", // 가입 (인증정보)
    "/settings", // 설정 (개인정보)
    "/account", // 계정 (개인정보)
    "/payment", // 결제 (결제정보)
    "/billing", // 청구 (금융정보)
    "/admin", // 관리자 (내부 데이터)
  ],
};

// URL별 녹화 여부 결정
export function shouldRecordUrl(pathname: string): boolean {
  // 명시적으로 비활성화된 페이지
  for (const disabledPath of PAGE_RECORDING_RULES.disabled) {
    if (pathname.startsWith(disabledPath)) {
      return false;
    }
  }

  // 명시적으로 활성화된 페이지
  for (const enabledPath of PAGE_RECORDING_RULES.enabled) {
    if (pathname.startsWith(enabledPath)) {
      return true;
    }
  }

  // 기본값: 활성화 (민감하지 않은 페이지)
  return true;
}

// 이벤트별 민감도 정의
export const SENSITIVE_EVENTS = {
  // 녹화 중에도 마스킹해야 할 이벤트
  masked: [
    "input_change", // 폼 입력
    "form_submit", // 폼 제출
    "text_selection", // 텍스트 선택
  ],

  // 녹화하지 않을 이벤트
  ignored: [
    "network_request", // 네트워크 요청
    "api_response", // API 응답
  ],
};

// 콘솔 로그 마스킹
export function shouldMaskConsoleLog(message: string): boolean {
  const sensitiveKeywords = [
    "password",
    "token",
    "secret",
    "credential",
    "apikey",
    "auth",
  ];

  return sensitiveKeywords.some((keyword) =>
    message.toLowerCase().includes(keyword),
  );
}
